import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import * as _ from 'lodash-es';
import { ClientLandingPageService } from './client-landing-page.service';
import { ClientDetailsModalComponent } from './client-details-modal/client-details-modal.component';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'gtw-client-landing-page',
    templateUrl: './client-landing-page.component.html',
    styleUrls: ['./client-landing-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ClientLandingPageComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  loading = true;
  clientLanDtls: any[] = [];
  searchClient = '';
  clientBtnLoader = false;
  selectedClientKey: any = {};
  clientName = '';
  private blobUrls: string[] = [];
  private dataLoaded = false;

  constructor(
    private clientLandingService: ClientLandingPageService,
    private dialog: MatDialog,
    private gtwSnackbarService: GtwSnackbarService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private appService: AppService,
    private globalService: GlobalService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.clientName = this.globalService.getClient()?.client_name || '';
    this.init();
  }

  ngOnDestroy(): void {
    this.blobUrls.forEach(url => URL.revokeObjectURL(url));
  }

  private init(): void {
    if (this.dataLoaded) {
      return;
    }
    this.dataLoaded = true;
    this.getClientLandingDetails();
  }

  private getClientLandingDetails(): void {
    this.clientLandingService.getClientLandingDtls(
      environment.base_url + '/getClientLandDtls'
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.loading = false;
        if (data.jsonObect && data.jsonObect.callSuccess !== '1' && data.jsonObect.clientLanDtls) {
          this.gtwSnackbarService.openSnackBar(
            data.errorMessage || 'Error loading client data',
            'gtw-snackbar--red'
          );
        } else {
          const rawList = data.jsonObect?.clientLanDtls || data.clientLanDtls || [];
          _.each(rawList, (item: any) => {
            const keys = Object.keys(item);
            const newItem: any = {};
            for (let i = 0; i < keys.length; i++) {
              newItem[keys[i].toUpperCase()] = item[keys[i]];
            }
            this.loadClientLogo(newItem);
            this.clientLanDtls.push(newItem);
          });
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.loading = false;
        console.error('Error fetching client landing details:', error);
        this.cdr.detectChanges();
      }
    );
  }

  private loadClientLogo(clientItem: any): void {
    if (!environment.admin_api_url || !clientItem.CLIENT_KEY) {
      return;
    }
    this.clientLandingService.downloadClientLogo(
      environment.admin_api_url,
      clientItem.CLIENT_KEY
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (blob: Blob) => {
        const urlCreator = window.URL || window.webkitURL;
        const blobUrl = urlCreator.createObjectURL(blob);
        this.blobUrls.push(blobUrl);
        clientItem.logo_client = this.sanitizer.bypassSecurityTrustUrl(blobUrl);
        this.cdr.detectChanges();
      },
      () => {
        console.log('Could not load logo for client:', clientItem.CLIENT_KEY);
      }
    );
  }

  get filteredClients(): any[] {
    if (!this.searchClient || this.searchClient.trim() === '') {
      return this.clientLanDtls;
    }
    const searchTerm = this.searchClient.toLowerCase();
    return this.clientLanDtls.filter((client: any) =>
      client.CLIENT_NAME && client.CLIENT_NAME.toLowerCase().includes(searchTerm)
    );
  }

  openClientDetailsModal(event: Event, client: any): void {
    if (this.clientBtnLoader) {
      return;
    }
    this.dialog.open(ClientDetailsModalComponent, {
      width: '650px',
      disableClose: true,
      panelClass: 'client-modal-dialog',
      data: {
        client: client,
        clientLanDtls: this.clientLanDtls
      }
    });
  }

  clientLanding(clientKey: string, route: string, stateLink: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.performClientSwitch(clientKey, route);
  }

  private performClientSwitch(clientKey: string, route: string): void {
    this.clientBtnLoader = true;
    this.selectedClientKey = clientKey;
    this.cdr.detectChanges();

    this.globalService.setClient(clientKey, environment.base_url).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        if (res?.callSuccess == '1') {
          const newKey = res.switch_to_client_key?.toString() || clientKey.toString();
          sessionStorage.setItem('client_key', newKey);
          this.clientLandingService.landingPage(route, this.appService, this.router);
        } else {
          this.clientBtnLoader = false;
          const failedClientName = this.resolveFailedClientName(res, clientKey);
          this.gtwSnackbarService.openSnackBar(
            `Unable to change the client: ${failedClientName}! Please Check Assigned: Workspaces OR Scenarios OR Modules etc.`,
            'gtw-snackbar--red'
          );
          this.cdr.detectChanges();
        }
      },
      () => {
        this.clientBtnLoader = false;
        this.gtwSnackbarService.openSnackBar(
          'Failed to switch client. Please try again.',
          'gtw-snackbar--red'
        );
        this.cdr.detectChanges();
      }
    );
  }

  private resolveFailedClientName(res: any, clientKey: string): string {
    const switchKey = res?.switch_to_client_key;
    if (switchKey !== undefined && switchKey !== null && switchKey !== '') {
      const matched = _.find(this.clientLanDtls, (c: any) =>
        String(c.CLIENT_KEY) === String(switchKey)
      );
      if (matched?.CLIENT_NAME) {
        return matched.CLIENT_NAME;
      }
    }
    const current = _.find(this.clientLanDtls, (c: any) =>
      String(c.CLIENT_KEY) === String(clientKey)
    );
    return current?.CLIENT_NAME || this.clientName || '';
  }

  trackByClientKey(index: number, client: any): any {
    return client ? client.CLIENT_KEY : index;
  }
}
